import { type MeasurementField } from "../types";
import { useT } from "../i18n";
import { tg } from "../telegram";

/**
 * Segmented On/Off control for `kind="bool"` indicators (parallel/isolated,
 * ok/no, on/off). The stored value is canonical lowercase `"on"` / `"off"`;
 * an empty string means "not recorded" (NULL in the DB). Tapping an active
 * option clears it back to empty.
 */
export function BoolToggle({
  value, onChange,
}: { value: string; onChange: (v: string) => void }) {
  const { t } = useT();
  const norm = value.trim().toLowerCase();
  const isOn = norm === "on" || norm === "1" || norm === "yes";
  const isOff = norm === "off" || norm === "0" || norm === "no";
  return (
    <div className="bool-toggle" role="group">
      <button
        type="button"
        className={"bool-opt" + (isOn ? " on" : "")}
        onClick={() => onChange(isOn ? "" : "on")}
      >
        {t("bool.on")}
      </button>
      <button
        type="button"
        className={"bool-opt" + (isOff ? " off" : "")}
        onClick={() => onChange(isOff ? "" : "off")}
      >
        {t("bool.off")}
      </button>
    </div>
  );
}

/**
 * Numeric input flanked by −/+ buttons that step the value by 1 for quick
 * adjustments. The input stays freely editable (decimal, comma or dot). The
 * step parses the current value (empty/invalid → 0), adds ±1, and rounds to
 * 3 decimals to avoid float dust. Buttons are out of the tab order so keyboard
 * users move input-to-input.
 */
export function NumberStepper({
  value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const step = (delta: number) => {
    const raw = (value ?? "").trim().replace(",", ".");
    const parsed = raw === "" ? 0 : parseFloat(raw);
    const base = Number.isFinite(parsed) ? parsed : 0;
    const next = Math.round((base + delta) * 1000) / 1000;
    tg()?.HapticFeedback?.selectionChanged?.();
    onChange(String(next));
  };
  return (
    <div className="stepper">
      <button
        type="button" className="stepper-btn" tabIndex={-1}
        aria-label="-1" onClick={() => step(-1)}
      >−</button>
      <input
        className="report-form-input stepper-input"
        type="text"
        inputMode="decimal"
        placeholder={placeholder ?? "—"}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      <button
        type="button" className="stepper-btn" tabIndex={-1}
        aria-label="+1" onClick={() => step(1)}
      >+</button>
    </div>
  );
}

/**
 * One labelled input row that renders the right control for a field's kind:
 *   bool   → segmented On/Off toggle (value stored as text)
 *   pair   → single text input accepting "X" or "X/Y"
 *   single → decimal input with −/+ stepper buttons
 *
 * Shared by the plan report form and the emergency extras list so bool/pair
 * fields behave identically in both places.
 */
export default function MeasurementFieldRow({
  field, value, onChange,
}: {
  field: MeasurementField;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useT();
  const labelSpan = (
    <span className="report-form-label">
      {field.label}
      {field.unit ? <span className="report-form-unit"> {field.unit}</span> : null}
    </span>
  );

  if (field.kind === "bool") {
    // No single focusable control → a <label> would have nothing to focus, so
    // render a plain div row instead.
    return (
      <div className="report-form-row">
        {labelSpan}
        <BoolToggle value={value} onChange={onChange} />
      </div>
    );
  }

  if (field.kind === "pair") {
    // "X/Y" — one text input, no stepper (ambiguous which half to step).
    return (
      <label className="report-form-row">
        {labelSpan}
        <input
          className="report-form-input"
          type="text"
          inputMode="decimal"
          placeholder={t("report_form.placeholder_pair")}
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      </label>
    );
  }

  // single numeric → input with −/+ stepper.
  return (
    <label className="report-form-row">
      {labelSpan}
      <NumberStepper
        value={value}
        onChange={onChange}
        placeholder={t("report_form.placeholder_single")}
      />
    </label>
  );
}
